// MDQ-specific scoring (compound criteria)
const calculateMDQScore = (answers) => {
  // Q1 has 13 sub-questions (yes/no), stored as array at index 0
  // Q2 is yes/no at index 1
  // Q3 is severity scale (0-3) at index 2
  // Q4 and Q5 are informational only at indices 3 and 4

  const q1Answers = answers[0]; // Array of yes/no (1/0) for 13 items
  const q2Answer = answers[1]; // 1 for yes, 0 for no
  const q3Answer = answers[2]; // 0-3 (0=No Problem, 1=Minor, 2=Moderate, 3=Serious)

  // Count "Yes" answers in Q1
  const q1YesCount = Array.isArray(q1Answers)
    ? q1Answers.filter(a => a === 1).length
    : 0;

  // Positive screen requires ALL three criteria:
  // 1. 7 or more "Yes" in Q1
  // 2. "Yes" to Q2
  // 3. "Moderate" or "Serious" (index 2 or 3) in Q3
  const isPositive = (q1YesCount >= 7) && (q2Answer === 1) && (q3Answer >= 2);

  return {
    isPositive,
    q1Count: q1YesCount,
    q2: q2Answer,
    q3: q3Answer,
    criteriaDetails: {
      q1Met: q1YesCount >= 7,
      q2Met: q2Answer === 1,
      q3Met: q3Answer >= 2
    }
  };
};

// PC-PTSD-5: gate + count of Yes answers
const calculatePCPTSD5Score = (answers) => {
  if (answers[0] === 0) return { total: 0, isPositive: false, gateAnswer: 'No' };
  const yesCount = answers.slice(1).filter(a => a === 1).length;
  return { total: yesCount, isPositive: yesCount >= 4, gateAnswer: 'Yes' };
};

// ASRS: total + Part A screener with per-item shaded thresholds
// Source: ASRS v1.1 Symptom Checklist, Page 1 of 2 darkly shaded boxes.
// Items 1-3 are shaded at Sometimes/Often/Very Often (answer >= 2),
// items 4-6 are shaded at Often/Very Often (answer >= 3).
// Positive screen = 4+ shaded marks across the 6 Part A items.
// The 18-item raw total (0-72) is informational only; the source explicitly
// states "No total score or diagnostic likelihood is utilized" for Part B.
const calculateASRSScore = (answers) => {
  const total = answers.reduce((sum, val) => sum + val, 0);
  const shadedThresholds = [2, 2, 2, 3, 3, 3]; // Q1-Q6 per-item shaded cutoffs
  const partAShadedCount = answers.slice(0, 6).filter((a, i) => a >= shadedThresholds[i]).length;
  return { total, partAShadedCount, isConsistent: partAShadedCount >= 4 };
};

// PANSS (Kay, Fiszbein & Opler, 1987 - Positive and Negative Syndrome Scale).
//
// Source: docs/official-scoring/sources/PANSS.pdf (Lundbeck/CNS Forum reprint
// of the Kay et al. 1987 instrument). 30 clinician-rated items grouped into
// three subscales by the section headers in the PDF:
//
//   Positive Scale (P)         items P1 - P7    indices  0 -  6   ( 7 items)
//   Negative Scale (N)         items N1 - N7    indices  7 - 13   ( 7 items)
//   General Psychopathology(G) items G1 - G16   indices 14 - 29   (16 items)
//
// Each item is rated on a 1-7 anchored Likert scale (PDF "Scoring Procedure"
// header on every item):
//   1 = Absent, 2 = Minimal, 3 = Mild, 4 = Moderate,
//   5 = Moderate severe, 6 = Severe, 7 = Extreme.
// The scale is **1-INDEXED**: a rating of "1 = Absent" contributes 1 point,
// not 0. There is no "0 = not assessed" missing-data anchor on the PANSS
// (unlike the BPRS). A completed PANSS administration therefore has every
// item in [1, 7] and the structural minimums are:
//
//   Positive subscale min  =  7  (all P items at 1)        max =  49 (all 7)
//   Negative subscale min  =  7  (all N items at 1)        max =  49 (all 7)
//   General  subscale min  = 16  (all G items at 1)        max = 112 (all 7)
//   Total min              = 30  (7 + 7 + 16)              max = 210 (49+49+112)
//   Composite (P - N)      = -42 (P all 1, N all 7)        max = +42 (P all 7, N all 1)
//
// A total below 30 is structurally impossible for a completed administration
// and indicates a malformed answer vector. Notably, score = 0 is the classic
// "0-indexed bug" symptom and would mean every answer was treated as 0.
//
// No reverse-keyed items, no weighted items. The Composite Score is defined
// in the original Kay et al. (1987) paper as:
//
//   Composite = Positive total - Negative total
//
// (range -42 to +42), used to characterize predominant syndrome polarity.
//
// The PRIMARY source PDF defines NO total-score interpretation bands. Any
// severity bands shipped in src/tests/panss/config.json are SECONDARY,
// from Leucht et al. (2005) "What does the PANSS mean?" Schizophrenia
// Research 79:231-238, which equipercentile-linked PANSS-Total to CGI-Severity
// anchors. Provenance is declared in config.json (`bandProvenance`), in this
// docstring, in src/utils/analysis.js, and in the analysis `desc` string.
//
// No `|| 0` silent defaults: a missing answer propagates as NaN and fails
// loudly at the range check.
const calculatePANSSScore = (answers) => {
  const positive = answers.slice(0, 7).reduce((s, v) => s + v, 0);
  const negative = answers.slice(7, 14).reduce((s, v) => s + v, 0);
  const general = answers.slice(14, 30).reduce((s, v) => s + v, 0);
  return {
    positive,
    negative,
    general,
    total: positive + negative + general,
    composite: positive - negative
  };
};

// CAPS-5: 20 symptom items (4 DSM-5 clusters) + 2 dissociation items
// Per PDF p. 3 rule 5, total severity = sum of items 1-20 (dissociation NOT in total).
// Per PDF p. 3 rule 6, cluster severity = sum of items in that cluster.
// Per DSM-5: Criterion B requires >=1 item in 1-5 rated >=2 (Moderate/threshold);
//            Criterion C requires >=1 item in 6-7 rated >=2;
//            Criterion D requires >=2 items in 8-14 rated >=2;
//            Criterion E requires >=2 items in 15-20 rated >=2.
// `criteriaItemMet` is the AND-gate of these four item-count rules ONLY; it does
// NOT establish a PTSD diagnosis. Criteria A, F, G, H require clinical judgement
// and the PAST WEEK version is not the right instrument for diagnostic status
// (PDF p. 4 NOTE).
const calculateCAPS5Score = (answers) => {
  const sumRange = (lo, hi) => {
    let s = 0;
    for (let i = lo; i < hi; i++) s += answers[i];
    return s;
  };
  const countAtThreshold = (lo, hi) => {
    let n = 0;
    for (let i = lo; i < hi; i++) if (answers[i] >= 2) n++;
    return n;
  };
  const clusterB = sumRange(0, 5);
  const clusterC = sumRange(5, 7);
  const clusterD = sumRange(7, 14);
  const clusterE = sumRange(14, 20);
  const total = clusterB + clusterC + clusterD + clusterE;
  const dissociation = answers[20] + answers[21];
  const bMetCount = countAtThreshold(0, 5);
  const cMetCount = countAtThreshold(5, 7);
  const dMetCount = countAtThreshold(7, 14);
  const eMetCount = countAtThreshold(14, 20);
  const bMet = bMetCount >= 1;
  const cMet = cMetCount >= 1;
  const dMet = dMetCount >= 2;
  const eMet = eMetCount >= 2;
  const criteriaItemMet = bMet && cMet && dMet && eMet;
  return {
    total, clusterB, clusterC, clusterD, clusterE, dissociation,
    bMetCount, cMetCount, dMetCount, eMetCount,
    bMet, cMet, dMet, eMet, criteriaItemMet
  };
};

// PID-5: 220 items, 25 facets, 5 domains, 16 reverse-scored items
const calculatePID5Score = (answers, questions) => {
  const reverseItems = [6,29,34,57,86,89,95,96,97,130,141,154,163,176,209,214]; // 0-indexed
  const adjusted = answers.map((a, i) => reverseItems.includes(i) ? 3 - a : a);
  const facets = questions?.config?.facets || {};
  const facetScores = {};
  for (const [name, indices] of Object.entries(facets)) {
    const items = indices.map(i => adjusted[i] || 0);
    const raw = items.reduce((s, v) => s + v, 0);
    facetScores[name] = { raw, average: parseFloat((raw / items.length).toFixed(2)), itemCount: items.length };
  }
  const domainDefs = {
    'Negative Affect': ['Emotional Lability', 'Anxiousness', 'Separation Insecurity'],
    'Detachment': ['Withdrawal', 'Anhedonia', 'Intimacy Avoidance'],
    'Antagonism': ['Manipulativeness', 'Deceitfulness', 'Grandiosity'],
    'Disinhibition': ['Irresponsibility', 'Impulsivity', 'Distractibility'],
    'Psychoticism': ['Unusual Beliefs & Experiences', 'Eccentricity', 'Perceptual Dysregulation']
  };
  const domainScores = {};
  for (const [domain, facetNames] of Object.entries(domainDefs)) {
    const avgs = facetNames.map(f => facetScores[f]?.average || 0);
    domainScores[domain] = parseFloat((avgs.reduce((s, v) => s + v, 0) / avgs.length).toFixed(2));
  }
  return { facetScores, domainScores };
};

// ISAA (Arya, 2008; MoSJE, Government of India)
//
// 40 items, each rated on a 1-5 Likert anchored to Rarely/Sometimes/
// Frequently/Mostly/Always (20% observation-frequency bands). The scale is
// 1-indexed, so the theoretical MINIMUM total is 40 (all 1s), not 0, and the
// maximum is 200 (all 5s). Six domains, fixed item order from the official
// PDF:
//   I.   Social Relationship and Reciprocity       items  1-9   (indices  0-8)
//   II.  Emotional Responsiveness                  items 10-14  (indices  9-13)
//   III. Speech-Language and Communication         items 15-23  (indices 14-22)
//   IV.  Behaviour Patterns                        items 24-30  (indices 23-29)
//   V.   Sensory Aspects                           items 31-36  (indices 30-35)
//   VI.  Cognitive Component                       items 37-40  (indices 36-39)
//
// Diagnostic bands (PDF Table-11, derived from ROC cut-off 70 and autism-group
// mean 106.09, SD 23.5):
//   < 70       Normal / No Autism
//   70  - 106  Mild Autism
//   107 - 153  Moderate Autism
//   > 153      Severe Autism
//
// Domain name strings are verbatim from the PDF section headers I-VI and
// MUST match the `domain` field used in src/tests/isaa/questions.json.
const calculateISAAScore = (answers) => {
  const total = answers.reduce((s, v) => s + v, 0);
  return {
    total,
    domains: {
      'Social Relationship and Reciprocity': answers.slice(0, 9).reduce((s, v) => s + v, 0),
      'Emotional Responsiveness': answers.slice(9, 14).reduce((s, v) => s + v, 0),
      'Speech-Language and Communication': answers.slice(14, 23).reduce((s, v) => s + v, 0),
      'Behaviour Patterns': answers.slice(23, 30).reduce((s, v) => s + v, 0),
      'Sensory Aspects': answers.slice(30, 36).reduce((s, v) => s + v, 0),
      'Cognitive Component': answers.slice(36, 40).reduce((s, v) => s + v, 0)
    }
  };
};

// BPRS (Overall & Gorham, 1962 - 18-item expanded version)
//
// Source: docs/official-scoring/sources/BPRS.pdf (Brief Psychiatric Rating
// Scale). The PDF header declares the rating scale:
//   0 = not assessed, 1 = not present, 2 = very mild, 3 = mild,
//   4 = moderate, 5 = moderately severe, 6 = severe, 7 = extremely severe.
//
// 18 symptom constructs:
//   1. Somatic Concern           10. Hostility
//   2. Anxiety                   11. Suspiciousness
//   3. Emotional Withdrawal      12. Hallucinatory Behavior
//   4. Conceptual Disorganization 13. Motor Retardation
//   5. Guilt Feelings            14. Uncooperativeness
//   6. Tension                   15. Unusual Thought Content
//   7. Mannerisms and Posturing  16. Blunted Affect
//   8. Grandiosity               17. Excitement
//   9. Depressive Mood           18. Disorientation
//
// Scoring model used here: the "0 = not assessed" anchor is a missing-data
// marker, NOT a valid completed response; a completed BPRS has every item
// rated 1-7. Minimum completed total is therefore 18 (all 1 = not present),
// NOT 0. Maximum is 126 (all 7 = extremely severe). A total below 18 is
// structurally impossible.
//
// The primary source PDF contains NO interpretation bands. The severity bands
// shipped in src/tests/bprs/config.json (18-31, 32-40, 41-52, 53-71, 72-126)
// are SECONDARY, sourced from Leucht et al. (2005) "Linking the PANSS, BPRS,
// and CGI: clinical implications" (Neuropsychopharmacology 30:2318-2325),
// which used equipercentile linking to map BPRS-Expanded totals onto the
// CGI-Severity anchors (Not ill / Mildly ill / Moderately ill / Markedly ill
// / Severely ill). Provenance is documented in docs/official-scoring/specs/
// BPRS.md and in the BPRS branch of analysis.js.
const calculateBPRSScore = (answers) => {
  // Total is a straight sum over 18 items. No reverse-keyed items, no
  // weighted items, no subscales recognized in the primary source.
  // No `|| 0` silent defaults: a missing answer will propagate as NaN and
  // fail loudly at the range check.
  return answers.reduce((s, v) => s + v, 0);
};

// IGDS9-SF: sum + disorder flag (5+ items scored max)
const calculateIGDS9SFScore = (answers) => {
  const total = answers.reduce((s, v) => s + v, 0);
  const criteriaMetCount = answers.filter(a => a === 5).length;
  return { total, criteriaMetCount, isDisordered: criteriaMetCount >= 5 };
};

// CPRS-R:S (Conners' Parent Rating Scale - Revised, Short form).
//
// Source: docs/official-scoring/sources/CPRS.pdf (Conners 1997, MHS). The
// PDF in this repo is the administration form only; it contains the 27 items
// and the 0-3 Likert legend but NO scoring key, item-to-subscale map, or
// T-score norm tables. The subscale assignments below are SECONDARY, from
// the Conners 1997 MHS manual as documented in Conners, Sitarenios, Parker,
// and Epstein (1998), "The Revised Conners' Parent Rating Scale (CPRS-R):
// Factor Structure, Reliability, and Criterion Validity", Journal of
// Abnormal Child Psychology 26(4):257-268.
//
// 27 items rated 0 (Not true at all) to 3 (Very much true).
// Four subscales (raw score ranges):
//   A: Oppositional                       6 items (indices 1,5,10,15,19,23)   0-18
//   B: Cognitive Problems/Inattention     6 items (indices 0,2,4,9,11,16)     0-18
//   C: Hyperactivity                      6 items (indices 3,6,13,17,22,25)   0-18
//   D: Conners' ADHD Index                9 items (indices 7,8,12,14,18,20,21,24,26)  0-27
// Coverage: 6+6+6+9 = 27, every item assigned exactly once. Total raw 0-81.
//
// T-score conversion (gender/age-normed, mean 50 SD 10, T>=65 clinically
// significant) is NOT performed: the MHS norm tables are proprietary and not
// in the source PDF. This handler returns RAW subscale scores plus the total;
// analysis.js flags the absence of T-score conversion in the desc string.
//
// No reverse-keyed items, no weighted items. No `|| 0` silent defaults: a
// missing answer propagates as NaN through the sum and fails loudly.
const calculateCPRSScore = (answers, questions) => {
  const subscales = questions?.config?.subscales || {};
  const rawScores = {};
  for (const [name, indices] of Object.entries(subscales)) {
    if (Array.isArray(indices)) {
      rawScores[name] = indices.reduce((sum, idx) => sum + answers[idx], 0);
    }
  }
  const total = answers.reduce((s, v) => s + v, 0);
  return { total, rawScores };
};

// CTRS-R:L (Conners' Teacher Rating Scale - Revised, Long form).
//
// Source: docs/official-scoring/sources/CTRS.pdf (Conners 1997, MHS, form
// CRS927). The PDF title bar reads "Conners' Teacher Rating Scale - Revised
// (L)" and the body enumerates items 1 through 59 on a 4-anchor 0-3 Likert:
//   0 = Not True At All (Never, Seldom)
//   1 = Just A Little True (Occasionally)
//   2 = Pretty Much True (Often, Quite a Bit)
//   3 = Very Much True (Very Often, Very Frequent)
//
// IMPORTANT FORM IDENTIFICATION: the shipped form is the LONG form (59
// items), NOT the Short form CTRS-R:S (28 items). Any prior documentation
// that described this file as "CTRS-R:S" with 28 items was in error.
// 59 items, 0-3 scale => raw total range 0 to 177.
//
// The source PDF is the administration sheet only; it contains NO scoring
// key, no item-to-subscale map, and no T-score norms. The subscale mapping
// below is derived from two provenance sources:
//
//   1. DSM-IV: Inattentive / Hyperactive-Impulsive / ADHD Symptoms Total
//      are PRIMARY-verifiable from the PDF: each assigned item's text is a
//      verbatim match to a DSM-IV ADHD criterion. The 9 Inattentive
//      criteria and 9 Hyperactive-Impulsive criteria are mapped by content
//      to items 3, 9, 12, 18, 27, 28, 49, 57, 58 (Inattentive) and items
//      11, 20, 29, 36, 39, 42, 44, 46, 55 (Hyperactive-Impulsive), 1-indexed.
//      The ADHD Symptoms Total subscale is the set union of the above
//      (18 items). These assignments are independently verifiable from
//      the PDF text alone.
//
//   2. Conners' Global Index (CGI-T) is SECONDARY: the 10-item CGI-T
//      (Restless-Impulsive 7 items + Emotional Lability 3 items) is
//      documented in the Conners 1997 MHS manual and widely reproduced in
//      the validation literature (e.g. Conners, Sitarenios, Parker, &
//      Epstein, 1998, "Revised CTRS-R: Factor Structure, Reliability, and
//      Criterion Validity", Journal of Abnormal Child Psychology
//      26(4):279-291). The CGI-T items are 2, 8, 14, 16, 34, 38, 44, 45,
//      54, 59 (1-indexed). All 10 items content-match restless-impulsive
//      or emotional-lability semantics.
//
// Subscales may OVERLAP: e.g. item 44 ("Fidgets with hands or feet or
// squirms in seat") appears in both CGI-T and DSM-IV Hyperactive-Impulsive,
// and item 45 ("Demands must be met immediately; easily frustrated") is
// only in CGI-T. As a result, the sum of subscale raws does NOT equal the
// total; this is by design and matches the Conners manual. Do NOT assert a
// coverage invariant of the CPRS form.
//
// The 6 factor-analytic CTRS-R:L subscales (Oppositional, Cognitive
// Problems/Inattention, Hyperactivity, Anxious-Shy, Perfectionism, Social
// Problems) from the Conners 1997 MHS manual are NOT mapped here: the
// exact item indices are proprietary to the MHS manual and cannot be
// independently verified from the source PDF. A future audit phase with
// manual access can add these four subscales to questions.config.subscales
// without code changes.
//
// T-score conversion (gender/age-normed, mean 50 SD 10, T>=65 clinically
// significant) is NOT performed: the MHS norm tables are proprietary and
// not in the source PDF. This handler returns RAW subscale scores plus the
// total; analysis.js flags the absence of T-score conversion.
//
// No reverse-keyed items, no weighted items. No `|| 0` silent defaults: a
// missing answer propagates as NaN through the sum and fails loudly.
const calculateCTRSScore = (answers, questions) => {
  const subscales = questions?.config?.subscales || {};
  const rawScores = {};
  for (const [name, indices] of Object.entries(subscales)) {
    if (Array.isArray(indices)) {
      rawScores[name] = indices.reduce((sum, idx) => sum + answers[idx], 0);
    }
  }
  const total = answers.reduce((s, v) => s + v, 0);
  return { total, rawScores };
};

// CAARS-S:L (Conners' Adult ADHD Rating Scale - Self-Report, Long form).
//
// Source: docs/official-scoring/sources/CAARS.pdf (Conners, Erhardt, Sparrow
// 1999, MHS). 66 items, 0-3 Likert, raw total range 0-198.
//
// Subscales (keyed off questions.config.subscales; 0-indexed):
//   A: Inattention/Memory Problems           12 items  max 36  (SECONDARY)
//   B: Hyperactivity/Restlessness            12 items  max 36  (SECONDARY)
//   C: Impulsivity/Emotional Lability        12 items  max 36  (SECONDARY)
//   D: Problems with Self-Concept             6 items  max 18  (SECONDARY)
//   E: DSM-IV Inattentive Symptoms            9 items  max 27  (PRIMARY content-match)
//   F: DSM-IV Hyperactive-Impulsive Symptoms  9 items  max 27  (PRIMARY content-match)
//   G: DSM-IV ADHD Symptoms Total            18 items  max 54  (PRIMARY, E union F, disjoint)
//   H: ADHD Index                            12 items  max 36  (SECONDARY)
//
// Subscales OVERLAP across the A-H set (e.g. several items appear in both an
// empirical factor subscale and a DSM-IV subscale), so the sum of subscale
// raws does NOT equal the total. E and F are disjoint by construction and
// their union equals G.
//
// The Inconsistency Index (INC) is computed as the sum of absolute
// differences across 8 paired items (questions.config.inconsistencyIndexPairs).
// INC >= 8 flags the protocol as potentially inconsistent per the PDF scoring
// sheet (p. 4). INC is reported alongside the subscale raws.
//
// T-score conversion is NOT performed. The MHS norm tables are proprietary
// and not in the source PDF. The analysis branch flags this explicitly.
const calculateCAARSScore = (answers, questions) => {
  const subscales = questions?.config?.subscales || {};
  const rawScores = {};
  for (const [name, indices] of Object.entries(subscales)) {
    if (Array.isArray(indices)) {
      rawScores[name] = indices.reduce((sum, idx) => sum + (answers[idx] || 0), 0);
    }
  }
  // Composite subscale G = DSM-IV Total (Inattentive + Hyperactive-Impulsive, disjoint union)
  const eKey = 'E: DSM-IV Inattentive Symptoms';
  const fKey = 'F: DSM-IV Hyperactive-Impulsive Symptoms';
  const gKey = 'G: DSM-IV ADHD Symptoms Total';
  if (gKey in subscales && eKey in rawScores && fKey in rawScores) {
    rawScores[gKey] = rawScores[eKey] + rawScores[fKey];
  }
  // Inconsistency Index: sum of |a - b| across the 8 paired items on the PDF scoring sheet.
  const pairs = questions?.config?.inconsistencyIndexPairs || [];
  let inconsistencyIndex = 0;
  for (const [i, j] of pairs) {
    inconsistencyIndex += Math.abs((answers[i] || 0) - (answers[j] || 0));
  }
  const inconsistencyFlag = pairs.length > 0 && inconsistencyIndex >= 8;
  return {
    rawScores,
    total: answers.reduce((s, v) => s + v, 0),
    inconsistencyIndex,
    inconsistencyFlag
  };
};

// YBOCS subscale helper: not wired into the main switch (the default sum path
// already produces the correct 0-40 total that the band-based getAnalysis expects).
// Tests import this directly to verify obsessions (Q1-5) and compulsions (Q6-10)
// subscale arithmetic without disturbing the analysis pipeline.
export const calculateYBOCSSubscales = (answers) => {
  const obsessions = answers.slice(0, 5).reduce((s, v) => s + v, 0);
  const compulsions = answers.slice(5, 10).reduce((s, v) => s + v, 0);
  return { obsessions, compulsions, total: obsessions + compulsions };
};

// ASSIST: per-substance scoring (stored as structured object)
const calculateASSISTScore = (answers) => {
  const data = answers[0];
  if (!data || !data.substancesUsed) return { substances: {}, hasHighRisk: false };
  const substances = {};
  for (const [sub, used] of Object.entries(data.substancesUsed)) {
    if (!used) continue;
    const r = data.responses?.[sub] || {};
    let score = (r.q2 || 0) + (r.q3 || 0) + (r.q4 || 0) + (r.q6 || 0) + (r.q7 || 0);
    if (sub !== 'Tobacco products') score += (r.q5 || 0);
    const isAlcohol = sub === 'Alcoholic beverages';
    const isTobacco = sub === 'Tobacco products';
    const lowMax = isTobacco ? 3 : isAlcohol ? 10 : 3;
    const risk = score <= lowMax ? 'Low' : score <= 26 ? 'Moderate' : 'High';
    substances[sub] = { score, risk };
  }
  return { substances, hasHighRisk: Object.values(substances).some(s => s.risk === 'High') };
};

export const calculateScore = (answers, testType, questions) => {
  switch (testType) {
    case 'MDQ': return calculateMDQScore(answers);
    case 'PCPTSD5': return calculatePCPTSD5Score(answers);
    case 'ASRS': return calculateASRSScore(answers);
    case 'PANSS': return calculatePANSSScore(answers);
    case 'CAPS5': return calculateCAPS5Score(answers);
    case 'PID5': return calculatePID5Score(answers, questions);
    case 'ISAA': return calculateISAAScore(answers);
    case 'IGDS9SF': return calculateIGDS9SFScore(answers);
    case 'ASSIST': return calculateASSISTScore(answers);
    case 'CAARS': return calculateCAARSScore(answers, questions);
    case 'CPRS': return calculateCPRSScore(answers, questions);
    case 'CTRS': return calculateCTRSScore(answers, questions);
    case 'BPRS': return calculateBPRSScore(answers);
    default: break;
  }

  // BSDS weighted scoring (keep existing logic)
  if (questions && questions.questions) {
    const questionArray = questions.questions;
    const hasWeightedQuestions = questionArray.some(q =>
      q.points !== undefined ||
      (q.options && Array.isArray(q.options) && q.options.some(opt => typeof opt === 'object' && opt.points !== undefined))
    );
    if (hasWeightedQuestions) {
      return answers.reduce((total, answer, idx) => {
        const question = questionArray[idx];
        if (question.questionType === 'checkbox') return total + (answer ? (question.points || 1) : 0);
        if (question.questionType === 'multiple_choice_weighted' && question.options) {
          return total + (question.options[answer]?.points || 0);
        }
        return total + answer;
      }, 0);
    }
  }

  // Default: simple sum (BDI, BAI, YBOCS, BPRS, IAT, YMRS)
  return answers.reduce((total, score) => total + score, 0);
};
